/**
 * ShriSewa cart enhancements.
 *
 * Loaded once (from sections/main-cart-items.liquid) with `defer`, so the DOM
 * is fully parsed before this runs. All interactions are bound via event
 * delegation on `document` because the elements they target (product-row
 * "Add" buttons, the discount form, the sticky checkout button) live inside
 * sections/main-cart-items.liquid and sections/main-cart-footer.liquid, both
 * of which get replaced wholesale via the Section Rendering API after every
 * cart mutation. Delegated listeners keep working after that replacement;
 * listeners bound directly to individual elements would not.
 */
(function () {
  'use strict';

  var STATUS_ID = 'shrisewa-cart-status';
  var SOURCE = 'shrisewa-cart-custom';
  var refreshing = false;

  /* ------------------------------------------------------------------ */
  /* Feedback                                                            */
  /*                                                                      */
  /* The status region is announced to screen readers AND visible, so a   */
  /* failed add ("sold out", "only 2 left") is not swallowed silently.    */
  /* ------------------------------------------------------------------ */

  var announceTimer = null;

  function announce(message, isError, autoHide) {
    var el = document.getElementById(STATUS_ID);
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('shrisewa-cart-status--error', !!isError);
    el.classList.toggle('shrisewa-cart-status--visible', !!message);

    if (announceTimer) clearTimeout(announceTimer);
    if (message && autoHide !== false) {
      announceTimer = setTimeout(function () {
        var current = document.getElementById(STATUS_ID);
        if (current) {
          current.classList.remove('shrisewa-cart-status--visible');
          current.textContent = '';
        }
      }, isError ? 6000 : 3000);
    }
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    // Dawn's spinner snippet ships with the `hidden` class (display:none !important),
    // so adding `loading` to the button is not enough on its own.
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  /* ------------------------------------------------------------------ */
  /* Section rendering                                                    */
  /*                                                                      */
  /* The Section Rendering API renders sections in the context of the URL */
  /* that is requested. The cart sections only exist on the cart          */
  /* template, so the request has to go to the cart page - requesting "/" */
  /* returns a 404 and the refresh silently does nothing.                 */
  /* ------------------------------------------------------------------ */

  function getSectionIds() {
    var itemsRoot = document.querySelector('cart-items[data-id]');
    var footerRoot = document.querySelector('#main-cart-footer[data-id]');
    return {
      items: itemsRoot ? itemsRoot.getAttribute('data-id') : null,
      footer: footerRoot ? footerRoot.getAttribute('data-id') : null,
      icon: document.getElementById('cart-icon-bubble') ? 'cart-icon-bubble' : null,
    };
  }

  function sectionsUrl() {
    // window.location.pathname keeps us on the cart template (and works for
    // localised /en-in/cart style URLs). Falls back to the themed cart route.
    var cartRoute = (window.routes && window.routes.cart_url) || '/cart';
    var path = window.location.pathname;
    return path.indexOf('/cart') !== -1 ? path : cartRoute;
  }

  function swapSection(sectionId, html) {
    if (!sectionId || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');

    // Sections that are part of the cart template are wrapped in
    // #shopify-section-<id> in the page, so the whole wrapper can be replaced.
    var freshEl = doc.getElementById('shopify-section-' + sectionId);
    var currentEl = document.getElementById('shopify-section-' + sectionId);
    if (freshEl && currentEl) {
      currentEl.replaceWith(freshEl);
      return;
    }

    // cart-icon-bubble lives in the header as a plain <a id="cart-icon-bubble">
    // with no section wrapper in the page, so only its contents get swapped -
    // this is the same approach Dawn's cart.js uses.
    var host = document.getElementById(sectionId);
    var source = freshEl || doc.querySelector('.shopify-section') || doc.body;
    if (host && source) {
      host.innerHTML = source.innerHTML;
    }
  }

  function refreshCartSections() {
    var ids = getSectionIds();
    var sectionList = [ids.items, ids.footer, ids.icon].filter(Boolean);
    if (!sectionList.length) return Promise.resolve();

    refreshing = true;

    return fetch(sectionsUrl() + '?sections=' + sectionList.join(','), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Could not refresh the cart (' + response.status + ').');
        }
        return response.json();
      })
      .then(function (data) {
        sectionList.forEach(function (id) {
          if (data[id]) swapSection(id, data[id]);
        });

        document.dispatchEvent(new CustomEvent('shrisewa:cart:refreshed'));

        // PUB_SUB_EVENTS is a top-level `const` in Dawn's constants.js, so it is
        // NOT a property of window - reference it as a bare global instead.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
          }
        } catch (e) {
          /* pubsub not loaded on this page - nothing to notify */
        }
      })
      .finally(function () {
        // Let Dawn's own cartUpdate handlers settle before we listen again.
        setTimeout(function () {
          refreshing = false;
        }, 0);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Keep the custom blocks in sync with Dawn's own cart updates          */
  /*                                                                      */
  /* Dawn only re-renders elements matching `.js-contents` after a        */
  /* quantity change or a line removal, which leaves the free shipping    */
  /* bar, savings message, upsell row and sticky total showing stale      */
  /* numbers. Re-render the whole sections when Dawn reports a change     */
  /* that did not come from us.                                          */
  /* ------------------------------------------------------------------ */

  function bindCartUpdateSync() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (refreshing) return;
      if (event && event.source === SOURCE) return;
      refreshCartSections();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartUpdateSync);
  } else {
    bindCartUpdateSync();
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart: Recommended Products + Cart Upsells                    */
  /* ------------------------------------------------------------------ */

  function handleAddToCart(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var quantity = parseInt(button.getAttribute('data-quantity'), 10);
    if (!quantity || quantity < 1) quantity = 1;

    button.disabled = true;
    setLoading(button, true);
    announce('Adding to cart…', false, false);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            // 422 = sold out / not enough inventory / quantity rule violation.
            throw new Error(data.description || data.message || 'Could not add this item to your cart.');
          }
          return data;
        });
      })
      .then(function () {
        return refreshCartSections();
      })
      .then(function () {
        announce('Added to cart.', false);
      })
      .catch(function (error) {
        announce(error.message || 'Could not add this item to your cart.', true);
        button.disabled = false;
        setLoading(button, false);
      });
    // On success the button no longer exists (its section was replaced), so
    // the re-enable only needs to happen in the failure path above.
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var addButton = target.closest('[data-product-add]');
    if (addButton) {
      event.preventDefault();
      handleAddToCart(addButton);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Discount code                                                       */
  /*                                                                      */
  /* Shopify's storefront has no AJAX endpoint to validate or apply a     */
  /* discount CODE against the cart object (cart.cart_level_discount_    */
  /* applications only reflects automatic discounts). The supported,     */
  /* non-fake mechanism for a code is navigating to /discount/<code>,     */
  /* which sets it for checkout and redirects back. That is a real        */
  /* navigation, not an in-place AJAX update - the cart page itself will  */
  /* not show a recalculated total for it, only checkout will.           */
  /* ------------------------------------------------------------------ */

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-discount-form]');
    if (!form) return;

    event.preventDefault();

    var input = form.querySelector('[data-discount-input]');
    var button = form.querySelector('[data-discount-submit]');
    var status = form.querySelector('[data-discount-status]');
    var code = input && input.value ? input.value.trim() : '';

    if (!status || !input || !button) return;

    if (!code) {
      status.textContent = form.getAttribute('data-empty-error') || 'Please enter a discount code.';
      status.classList.add('shrisewa-discount__status--error');
      input.focus();
      return;
    }

    if (button.disabled) return;

    button.disabled = true;
    setLoading(button, true);
    status.textContent = '';
    status.classList.remove('shrisewa-discount__status--error');

    var cartRoute = (window.routes && window.routes.cart_url) || '/cart';
    window.location.href =
      '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(cartRoute);
  });

  /* ------------------------------------------------------------------ */
  /* Sticky checkout: guard against duplicate submissions                */
  /*                                                                      */
  /* Do NOT set button.disabled inside the click handler: a submit        */
  /* button's activation behaviour runs after click listeners, and a      */
  /* disabled button has no activation behaviour - the form would never   */
  /* be submitted. Disable on the form's submit event instead.           */
  /* ------------------------------------------------------------------ */

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.id !== 'cart') return;
      if (event.defaultPrevented) return;

      var buttons = document.querySelectorAll(
        '.shrisewa-sticky-checkout__button, #checkout'
      );
      // Disable asynchronously so the submission that is already in flight
      // is not cancelled.
      setTimeout(function () {
        Array.prototype.forEach.call(buttons, function (button) {
          button.disabled = true;
          button.classList.add('loading');
        });
      }, 0);

      // If the user comes back via the browser's back/forward cache, the
      // buttons must work again.
      window.addEventListener('pageshow', function () {
        Array.prototype.forEach.call(buttons, function (button) {
          button.disabled = false;
          button.classList.remove('loading');
        });
      });
    },
    true
  );
})();
